import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ArrowLeft, Save, Trash2, CheckCircle2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { workpapersAPI, WORKPAPER_STATUS_LABELS, WORKPAPER_STATUS_COLORS } from '../lib/workpapersAPI';
import { formatAUDate } from '../lib/dateUtils';

export default function WorkpaperDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [wp, setWp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [reviewComments, setReviewComments] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await workpapersAPI.get(id);
      setWp(res.data);
      setItems(res.data.items || []);
      setNotes(res.data.accountant_notes || '');
      setReviewComments(res.data.review_comments || '');
    } catch (error) {
      toast.error('Failed to load workpaper');
      navigate('/workpapers');
    } finally {
      setLoading(false);
    }
  };

  const locked = wp && (wp.status === 'partner_signoff' || wp.status === 'completed');

  const toggleItem = (itemId, checked) => {
    setItems((arr) => arr.map((i) => i.id === itemId ? { ...i, completed: checked } : i));
  };
  const setItemValue = (itemId, value) => {
    setItems((arr) => arr.map((i) => i.id === itemId ? { ...i, value } : i));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await workpapersAPI.update(id, {
        items,
        accountant_notes: notes,
        review_comments: reviewComments,
      });
      setWp(res.data);
      setItems(res.data.items || []);
      toast.success('Workpaper saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSignoff = async (role) => {
    try {
      const res = await workpapersAPI.signoff(id, role);
      setWp(res.data);
      toast.success(`${role === 'reviewer' ? 'Reviewer' : 'Partner'} sign-off recorded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sign-off failed');
    }
  };

  const handleDelete = async () => {
    try {
      await workpapersAPI.delete(id);
      toast.success('Workpaper deleted');
      navigate('/workpapers');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading || !wp) {
    return <div className="p-6"><div className="animate-pulse h-64 bg-muted rounded"></div></div>;
  }

  const hasReviewer = wp.signoffs?.some((s) => s.role === 'reviewer');
  const hasPartner = wp.signoffs?.some((s) => s.role === 'partner');
  const doneCount = items.filter((i) => i.completed).length;

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/workpapers')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Workpapers
        </Button>
        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700"
          onClick={() => setDeleteOpen(true)} disabled={wp.status === 'completed'}
          title={wp.status === 'completed' ? 'Completed workpapers cannot be deleted' : 'Delete'}>
          <Trash2 className="w-4 h-4 mr-2" /> Delete
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="wp-detail-title">
          {wp.name}
        </h1>
        <Badge className={WORKPAPER_STATUS_COLORS[wp.status]}>{WORKPAPER_STATUS_LABELS[wp.status]}</Badge>
        {locked && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Lock className="w-3 h-3" /> Locked</span>}
      </div>
      <p className="text-muted-foreground -mt-3">
        {wp.client_name}{wp.financial_year ? ` · ${wp.financial_year}` : ''} · {doneCount}/{items.length} items complete
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Line items */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              <span>Working Papers</span>
              {!locked && (
                <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={handleSave} disabled={saving} data-testid="save-workpaper">
                  <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {items.map((it) => (
              <div key={it.id} className="space-y-1 border-b pb-3 last:border-0" data-testid={`wp-item-${it.id}`}>
                <div className="flex items-center gap-3">
                  <Checkbox checked={it.completed} disabled={locked} onCheckedChange={(c) => toggleItem(it.id, c === true)} />
                  <span className={`text-sm ${it.completed ? 'text-muted-foreground' : 'text-foreground'}`}>{it.label}</span>
                </div>
                <Input
                  className="ml-7 text-sm" placeholder="Working / figure / note..."
                  value={it.value || ''} disabled={locked}
                  onChange={(e) => setItemValue(it.id, e.target.value)}
                />
              </div>
            ))}

            <div className="space-y-2 pt-2">
              <Label>Accountant Notes</Label>
              <Textarea rows={3} value={notes} disabled={locked} onChange={(e) => setNotes(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Review Comments</Label>
              <Textarea rows={2} value={reviewComments} disabled={locked} onChange={(e) => setReviewComments(e.target.value)} />
            </div>
          </CardContent>
        </Card>

        {/* Sign-off chain */}
        <Card>
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Sign-off</CardTitle></CardHeader>
          <CardContent className="space-y-4 text-sm">
            <p className="text-xs text-muted-foreground">Reviewer signs first, then partner. Partner sign-off completes and locks the workpaper.</p>

            <SignoffRow
              label="Reviewer" done={hasReviewer}
              signoff={wp.signoffs?.find((s) => s.role === 'reviewer')}
              onSign={() => handleSignoff('reviewer')}
              disabled={hasReviewer}
            />
            <SignoffRow
              label="Partner" done={hasPartner}
              signoff={wp.signoffs?.find((s) => s.role === 'partner')}
              onSign={() => handleSignoff('partner')}
              disabled={hasPartner || !hasReviewer}
              hint={!hasReviewer ? 'Reviewer must sign first' : null}
            />
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this workpaper?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes "{wp.name}". This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SignoffRow({ label, done, signoff, onSign, disabled, hint }) {
  return (
    <div className="border rounded-lg p-3">
      <div className="flex items-center justify-between">
        <span className="font-medium text-foreground/90">{label}</span>
        {done ? (
          <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" /> Signed</Badge>
        ) : (
          <Button size="sm" variant="outline" onClick={onSign} disabled={disabled} data-testid={`signoff-${label.toLowerCase()}`}>
            Sign off
          </Button>
        )}
      </div>
      {done && signoff && (
        <p className="text-xs text-muted-foreground mt-1">{signoff.user_name} · {formatAUDate(signoff.signed_at)}</p>
      )}
      {!done && hint && <p className="text-xs text-amber-600 mt-1">{hint}</p>}
    </div>
  );
}
